import {
    Box,
    Button,
    Flex,
    Heading,
    Stack,
    Text,
    ThemeColorVariable,
    ThemeFontSizeVariable,
    ThemeSpaceVariable,
    useToast,
} from "@caspeco/casper-ui-library";
import { useWebBooking } from "api/api-hooks/use-web-booking";
import { useBookingMenuRespond } from "api/api-hooks/use-booking-menu-respond";
import BookingPreorderSkeleton from "components/booking-preorder/booking-preorder-skeleton";
import ExpiredLink from "components/error-components/expired-link";
import PreorderBody from "components/selection-components/preorder/preorder-body";
import { usePreorderValidationNavigation } from "components/selection-components/preorder/use-preorder-validation-navigation";
import BackButton from "components/stepper/back-button";
import { PreorderContext } from "context/preorder-context";
import { PreorderProvider } from "context/preorder-provider";
import i18n from "i18next";
import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Routes } from "routes";
import { BookingMenu } from "types/menu";
import { MenuGroupType } from "types/menu-group";
import { WebBooking } from "types/web-booking";
import { getMenuProposalResponseMenu } from "utils/menu-proposal-flow";

function MenuSelectLoading() {
    return (
        <Box maxW={["100%", "560px"]} width="100vw">
            <BookingPreorderSkeleton />
        </Box>
    );
}

function MenuSelectExpiredMessage() {
    return <ExpiredLink />;
}

interface MenuSelectContentProps {
    booking: WebBooking;
    menu: BookingMenu;
}

function MenuSelectContent({ booking, menu }: MenuSelectContentProps) {
    const { system, bookingId } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { showToast } = useToast();
    const updateBookingMenu = useBookingMenuRespond({ showGlobalErrorModal: false });
    const { currentQuantities, setCurrentQuantities, currentTotalQuantity, isPreorderValid } =
        useContext(PreorderContext) || {};
    const navigateToInvalidGroup = usePreorderValidationNavigation();

    const [blockInitialQuantities, setBlockInitialQuantities] = useState(false);

    const myBookingPath = `/${Routes.BOOKING}/${system}/${bookingId}`;

    const guestText = t("start.numberGuests", { guests: booking.guests });
    const dateText = booking.start.locale(i18n.language).format("D MMM YYYY");

    // Pre-populate quantities from the sent menu proposal response.
    useEffect(() => {
        const previousQuantities = new Map<number, number>();
        menu.groups?.forEach((group) => {
            group.items?.forEach((item) => {
                previousQuantities.set(item.articleID, item.quantity);
            });
        });

        const guestCount = booking.guests || 1;
        const initialQuantities =
            menu.groups?.map((group) =>
                group.groupType === MenuGroupType.IncludedPerGuest
                    ? (group.items ?? []).map(() => guestCount)
                    : (group.items ?? []).map(
                          (item) => previousQuantities.get(item.articleID) ?? item.quantity
                      )
            ) ?? [];

        setBlockInitialQuantities(true);
        setCurrentQuantities?.(initialQuantities);
    }, [booking.guests, menu, setCurrentQuantities]);

    const onSaveSelection = async () => {
        if (!menu || !currentQuantities) return;

        if (isPreorderValid === false) {
            navigateToInvalidGroup();
            return;
        }

        const bookingMenuWithChoices: BookingMenu = {
            ...menu,
            groups: menu.groups?.map((group, groupIndex) => ({
                ...group,
                items: group.items?.map((item, itemIndex) => ({
                    ...item,
                    quantity: currentQuantities[groupIndex]?.[itemIndex] ?? item.quantity,
                })),
            })),
        };

        try {
            await updateBookingMenu.mutateAsync({
                bookingMenu: bookingMenuWithChoices,
                isReRespond: false,
            });
            showToast({
                type: "success",
                title: t("menuProposal.savedTitle"),
                description: t("menuProposal.savedDescription"),
                duration: 5000,
            });
            void navigate(myBookingPath, {
                replace: true,
                state: { openBookingDetails: true },
            });
        } catch {
            showToast({
                type: "error",
                description: t("errorInformation.somethingWrong"),
                duration: null,
            });
        }
    };

    const onDecideLater = () => {
        void navigate(myBookingPath);
    };

    const hasSelection = (currentTotalQuantity ?? 0) > 0;

    return (
        <Stack spacing={ThemeSpaceVariable.None}>
            <Box
                px={ThemeSpaceVariable.Medium}
                pt={ThemeSpaceVariable.Medium}
                pb={ThemeSpaceVariable.Medium}
                bg={ThemeColorVariable.Background}
            >
                <Flex w="full" align="center" textAlign="center">
                    <Flex flex={1} align="center">
                        <BackButton onClick={() => void navigate(myBookingPath)} />
                    </Flex>
                    <Flex direction="column" align="center" flex={3}>
                        {(guestText || dateText) && (
                            <Flex
                                justify="center"
                                direction="row"
                                gap={ThemeSpaceVariable.XSmall}
                                flexWrap="wrap"
                                align="center"
                            >
                                {[guestText, dateText].filter(Boolean).map((text, index, arr) => (
                                    <Text
                                        key={index}
                                        as="span"
                                        fontSize={ThemeFontSizeVariable.Small}
                                        color={ThemeColorVariable.OnBackgroundSubdued}
                                    >
                                        {text}
                                        {index !== arr.length - 1 && ","}
                                    </Text>
                                ))}
                            </Flex>
                        )}
                        <Heading
                            as="h2"
                            fontWeight="bold"
                            fontSize={ThemeFontSizeVariable.Medium}
                            lineHeight={1.5}
                        >
                            {t("stepper.preorder")}
                        </Heading>
                    </Flex>
                    <Flex flex={1} />
                </Flex>
            </Box>
            <Box maxW={["100%", "560px"]} width="100vw">
                <PreorderBody
                    preorderHeaderName={menu.name}
                    blockInitialQuantities={blockInitialQuantities}
                    overrideMenu={menu}
                />
            </Box>
            <Stack
                px={ThemeSpaceVariable.Medium}
                pt={ThemeSpaceVariable.Medium}
                spacing={ThemeSpaceVariable.Small}
            >
                <Button
                    variant="primary"
                    size="xl"
                    w="full"
                    isDisabled={!hasSelection}
                    isLoading={updateBookingMenu.isPending}
                    onClick={() => void onSaveSelection()}
                    data-track-click="menu-select-save"
                >
                    {t("menuProposal.saveSelection", { count: currentTotalQuantity ?? 0 })}
                </Button>
                <Button
                    variant="ghost"
                    size="xl"
                    w="full"
                    onClick={onDecideLater}
                    data-track-click="menu-select-decide-later"
                >
                    {t("menuProposal.decideLater")}
                </Button>
            </Stack>
        </Stack>
    );
}

export default function MenuSelectView() {
    const { data: booking, isPending } = useWebBooking();
    const menu = getMenuProposalResponseMenu(booking);

    // This is an optional post-booking menu response flow, separate from the wizard preorder step even though it reuses the preorder UI.
    if (isPending) {
        return <MenuSelectLoading />;
    }

    if (!booking || !menu) {
        return <MenuSelectExpiredMessage />;
    }

    return (
        <PreorderProvider>
            <MenuSelectContent booking={booking} menu={menu} />
        </PreorderProvider>
    );
}
